import { Injectable, Injector } from '@angular/core';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { sanitizeLogMessage } from '../../functions/string-format.functions';
import { AppInsightsService } from './app-insights.service';
const DEFAULT_PREFIX = '[EGL]';

@Injectable({
    providedIn: 'root',
})
export class LoggerService {
    constructor(
        private appInsightsSrv: AppInsightsService,
        private injector: Injector,
    ) {}

    debug(msg: string, ...optionalParams: any[]): void {
        this.writeLogs(msg, optionalParams, SeverityLevel.Verbose, 'info');
    }

    info(msg: string, ...optionalParams: any[]): void {
        this.writeLogs(msg, optionalParams, SeverityLevel.Information, 'info');
    }

    writeLogs(msg: string, optionalParams: any[], severityLevel: SeverityLevel, consoleType: string): void {
        msg = sanitizeLogMessage(msg);
        optionalParams = this.sanitizeOptParams(optionalParams);
        console[consoleType](this.getFormattedMsg(msg), ...optionalParams);

        //Per limiti di app insight è necessario dividere la loggata in n log, ognuno di 8000 caratteri
        const seen = new Set();
        (
            JSON.stringify(optionalParams, (key, value) => {
                if (typeof value === 'object' && value !== null) {
                    if (seen.has(value)) {
                        return;
                    }
                    seen.add(value);
                }
                return value;
            }).match(new RegExp('.{1,' + 6000 + '}', 'g')) || []
        ).forEach((split, idx, arr) => {
            const fullMsg = msg + (arr.length > 1 ? ' - ' + idx : '');
            this.appInsightsSrv.logTrace({ message: this.getAIMessage(fullMsg), severityLevel: severityLevel }, [
                split,
            ]);
        });
    }

    toastInfo(msg: string, ...optionalParams: any[]): void {
        this.info(msg, ...optionalParams);
        if (this.toastrSrv) {
            const title = this.translateSrv.instant('COMMON.INFO_ALERT');
            this.toastrSrv.info(msg, title);
        }
    }

    toastSuccess(title: string, msg: string, ...optionalParams: any[]): void {
        this.info(msg, ...optionalParams);
        if (this.toastrSrv) {
            const toastTitle = title || this.translateSrv.instant('COMMON.INFO_ALERT');
            this.toastrSrv.success(msg, toastTitle);
        }
    }

    toastError(msg: string, title?: string, enableHtml?: boolean): void {
        this.info(msg, title);
        if (this.toastrSrv) {
            this.toastrSrv.error(msg, title || 'Attenzione', { enableHtml: !!enableHtml });
        }
    }

    warn(msg: string, showAlert?: boolean, ...optionalParams: any[]): void {
        this.writeLogs(msg, optionalParams, SeverityLevel.Warning, 'warn');
        if (showAlert && this.toastrSrv) {
            const title = this.translateSrv.instant('COMMON.INFO_ALERT');
            this.toastrSrv.warning(msg, title);
        }
    }

    error(title?: string, msg?: string, error?: any, showAlert?: boolean, ...optionalParams: any[]): void {
        title = sanitizeLogMessage(title);
        msg = sanitizeLogMessage(msg);
        optionalParams = this.sanitizeOptParams(optionalParams);
        title = title || 'Attenzione';
        msg = msg || 'Si è verificato un errore';
        let fullMsg = `${title}: ${msg}`;
        const logParams = optionalParams && error ? optionalParams.concat(error) : optionalParams || error;
        const errorMessage = typeof error === 'string' ? error : error?.message;

        fullMsg +=
            error?.status || errorMessage
                ? ` ERROR STATUS: ${error?.status || 'null'} - MESSAGE: ${errorMessage}`
                : error
                  ? ` - ${error}`
                  : '';
        this.writeLogs(fullMsg, logParams, SeverityLevel.Error, 'error');
        if (showAlert && this.toastrSrv) {
            this.toastrSrv.error(msg, title);
        }
    }

    private getFormattedMsg(msg: string): string {
        // use defaultPrefix only if message string not start with prefix [...]
        return /^\[[A-Z0-9]+\] /.test(msg || '') ? msg : `${DEFAULT_PREFIX} ${msg}`;
    }

    private sanitizeOptParams(params: any[]): any[] {
        if (!params) {
            return params;
        }
        return params.map((p) => (typeof p === 'string' ? sanitizeLogMessage(p) : p));
    }

    private getAIMessage(message: string): string {
        return `[SUP] ${message}`;
    }

    private get toastrSrv(): ToastrService {
        return this.injector.get(ToastrService);
    }

    public get translateSrv(): TranslateService {
        return this.injector.get(TranslateService);
    }
}
